<template>
  <div class="app-shell">
    <aside class="app-sidebar">
      <div class="app-sidebar__brand">
        <div class="app-sidebar__brand-mark">TH</div>
        <div>
          <div class="app-sidebar__brand-name">Form Research</div>
          <div class="app-sidebar__brand-sub">Internal Tools</div>
        </div>
      </div>
      <nav class="app-sidebar__nav">
        <div v-for="group in navGroups" :key="group.label" class="app-sidebar__group">
          <div class="app-sidebar__group-label">{{ group.label }}</div>
          <router-link
            v-for="link in group.links"
            :key="link.name"
            class="app-sidebar__link"
            :class="{ 'is-active': $route.name === link.name }"
            :to="{ name: link.name }"
          >
            <span class="app-sidebar__icon" v-html="link.icon" />
            <span class="app-sidebar__link-text">{{ link.label }}</span>
          </router-link>
        </div>
      </nav>
      <div class="app-sidebar__footer">
        <div>{{ store.indexName }}</div>
        <div>{{ store.opensearchUrl.replace(/^https?:\/\//, '') }}</div>
      </div>
    </aside>

    <header class="app-topbar">
      <h1 class="app-topbar__title">{{ $route.meta?.title || 'TaxHawk Form Research' }}</h1>
      <span class="app-topbar__sub">{{ subtitle }}</span>
      <div class="app-topbar__status">
        <span v-if="ingestRunning" class="d-flex align-items-center" style="gap: 8px;">
          <span class="status-dot is-busy" />
          Ingest running · {{ progressLabel }}
        </span>
        <span v-else-if="store.opensearchHealthy" class="d-flex align-items-center" style="gap: 8px;">
          <span class="status-dot is-healthy" />
          {{ chunkCountLabel }} chunks indexed
        </span>
        <span v-else class="d-flex align-items-center" style="gap: 8px;">
          <span class="status-dot is-error" />
          OpenSearch unreachable
        </span>
        <button class="btn btn-sm btn-outline-secondary" @click="store.refreshAll()" :disabled="store.loading">
          <span v-if="store.loading" class="spinner me-1" />
          Refresh
        </button>
      </div>
    </header>

    <main class="app-main">
      <router-view v-slot="{ Component }">
        <transition name="fade" mode="out-in">
          <component :is="Component" />
        </transition>
      </router-view>
    </main>
  </div>
</template>

<script setup>
import { computed, onMounted, onBeforeUnmount } from 'vue';
import { useResearchStore } from '@/stores/research';

const store = useResearchStore();

const navIcons = {
  dashboard: `<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="3" width="7" height="9"/><rect x="14" y="3" width="7" height="5"/><rect x="14" y="12" width="7" height="9"/><rect x="3" y="16" width="7" height="5"/></svg>`,
  ask: `<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"/></svg>`,
  forms: `<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>`,
  updates: `<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 12a9 9 0 1 1-3-6.7L21 8"/><polyline points="21 3 21 8 16 8"/></svg>`,
  diff: `<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M6 3v12"/><circle cx="6" cy="18" r="3"/><path d="M18 6a3 3 0 1 0-3 3"/><path d="M18 9v12"/></svg>`,
  history: `<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>`,
  jurisdictions: `<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/></svg>`,
  analyses: `<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 2l2.4 5 5.6.8-4 4 1 5.6L12 14.8 6.6 17.4l1-5.6-4-4 5.6-.8z"/></svg>`,
  ingest: `<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/></svg>`,
  failures: `<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>`,
  hackathon: `<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="4" width="18" height="14" rx="2"/><path d="M8 22h8"/><path d="M12 18v4"/><path d="M8 9h8"/><path d="M8 13h5"/></svg>`,
  taxlogic: `<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="9" y1="13" x2="15" y2="13"/><line x1="9" y1="17" x2="13" y2="17"/></svg>`,
};

const navGroups = [
  {
    label: 'Overview',
    links: [
      { name: 'dashboard', label: 'Dashboard', icon: navIcons.dashboard },
      { name: 'jurisdictions', label: 'Jurisdictions', icon: navIcons.jurisdictions },
      { name: 'updates', label: 'Updates Feed', icon: navIcons.updates },
    ],
  },
  {
    label: 'Research',
    links: [
      { name: 'ask', label: 'Ask the Agent', icon: navIcons.ask },
      { name: 'forms', label: 'Form Search', icon: navIcons.forms },
      { name: 'history', label: 'Form History', icon: navIcons.history },
    ],
  },
  {
    label: 'Compare',
    links: [
      { name: 'diff', label: 'Year Diff', icon: navIcons.diff },
      { name: 'analyses', label: 'AI Analyses', icon: navIcons.analyses },
    ],
  },
  {
    label: 'Generate',
    links: [
      { name: 'taxlogic', label: 'Outline Draft', icon: navIcons.taxlogic },
    ],
  },
  {
    label: 'Operations',
    links: [
      { name: 'ingest', label: 'Ingest Status', icon: navIcons.ingest },
      { name: 'failures', label: 'Failed Documents', icon: navIcons.failures },
    ],
  },
  {
    label: 'Demo',
    links: [
      { name: 'hackathon', label: 'Hackathon', icon: navIcons.hackathon },
    ],
  },
];

const subtitle = computed(() => {
  if (store.manifestDocs.length) {
    return `${store.manifestDocs.length.toLocaleString()} documents tracked · ${Object.keys(store.jurisdictionStats).length} jurisdictions`;
  }
  return 'TaxHawk internal tooling';
});

const ingestRunning = computed(() => store.progress?.status === 'running');
const progressLabel = computed(() => {
  if (!store.progress) return '';
  const { processed, total } = store.progress;
  return `${processed ?? 0} / ${total ?? 0}`;
});
const chunkCountLabel = computed(() => (store.indexCount || 0).toLocaleString());

let poll;
onMounted(() => {
  store.refreshAll();
  poll = setInterval(() => {
    store.refreshProgress();
  }, 10000);
});
onBeforeUnmount(() => clearInterval(poll));
</script>

<style scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 120ms ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
